/**
 * useRejectionPoller — poll-based PENDING→REJECTED transition detection.
 *
 * Phase 9 / Plan 09-04 — compares the current jobs array against the previous
 * snapshot. When a job transitions from a non-REJECTED state to REJECTED, fires:
 *   1. A Sonner `toast.error` with exact UI-SPEC copy (title "Job Rejected",
 *      description "{driverName} rejected {haulId} — {reason}", duration 8s).
 *   2. `notificationStore.addRejection(job)` → increments the bell's unread count.
 *
 * Deduplication: prevJobsRef holds the last-seen statusCode per jobId.
 * A job already at REJECTED on the NEXT poll does NOT re-fire (prev === 'REJECTED').
 * This implements the dedupe requirement from DISPATCH-05.
 *
 * The hook is mounted by OperationsPage so rejection toasts fire while the
 * dispatcher is on the Operations view (the primary working surface).
 *
 * Spec: PATTERNS.md useRejectionPoller.ts section; UI-SPEC §7 (rejection toast);
 *       DISPATCH-05; T-09-04-05.
 */

import { useRef, useEffect } from 'react';
import { toast } from 'sonner';
import type { DispatchJob } from '../jobs/use-jobs';
import { useNotificationStore } from './notification-store';

/**
 * Detects PENDING→REJECTED (or any non-REJECTED→REJECTED) transitions in the
 * jobs array and fires toast + notification-store side effects.
 *
 * @param jobs The current jobs snapshot from `useJobs`. May be an empty array.
 */
export function useRejectionPoller(jobs: readonly DispatchJob[]): void {
  // Map of jobId → last-seen statusCode. Persists across renders without
  // causing re-renders (useRef, not useState).
  const prevJobsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (jobs.length === 0) {
      // Update ref even on empty to avoid stale entries on reconnect.
      prevJobsRef.current = new Map();
      return;
    }

    const addRejection = useNotificationStore.getState().addRejection;

    jobs.forEach((job) => {
      const prev = prevJobsRef.current.get(job.id);

      // Fire only on the FIRST tick a job reaches REJECTED status.
      // If prev === 'REJECTED' the job was already rejected last poll — no re-fire.
      if (prev !== 'REJECTED' && job.statusCode === 'REJECTED') {
        const driverName = job.driverName ?? 'Unknown driver';
        // Reason is not surfaced in the list endpoint today; use a sensible default.
        // Plan 09-05 can enrich this when the detail endpoint is wired.
        const reason = 'no reason provided';

        // UI-SPEC §7 toast: title "Job Rejected", 8s auto-dismiss, action "View Rejected Job"
        toast.error('Job Rejected', {
          description: `${driverName} rejected ${job.haulId ?? '—'} — ${reason}`,
          action: {
            label: 'View Rejected Job',
            onClick: (): void => {
              // Scroll/focus the rejected row. The DOM id is set on the <tr> in JobQueue.
              const el = document.getElementById(`job-row-${job.id}`);
              if (el !== null) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                el.focus();
              }
            },
          },
          duration: 8_000,
        });

        addRejection({
          id: job.id,
          haulId: job.haulId ?? '—',
          driverName,
          reason,
        });
      }
    });

    // Update the reference map AFTER processing so next poll can compare.
    prevJobsRef.current = new Map(jobs.map((j) => [j.id, j.statusCode]));
  }, [jobs]);
}
